<template>
	<div class="fight-status-detail" :class="skin">
		<div
			v-if="
				entity.index ===
				tilerepo.getBlockedByEnemiesMainEnemyTileIndexByTileIndex(
					entity.index,
				)
			"
			class="chieftain-crown"
		/>
		<div
			v-if="!tilerepoFriendlySupplyUnitsFrameNames.has(entity.frameName)"
			class="fight-status-detail__header"
		>
			<div>{{ translate("ui.health") }}:</div>
			<div class="health-value">
				<div class="health-value__wrapper">
					<div class="health-value__total">
						{{ formatNumber(currentHp) }}
					</div>
					<div class="health-value__max">
						/{{ formatNumber(maxHp) }}
					</div>
				</div>
				<AppMainIcon iconName="hearth" :iconSize="18" />
			</div>
		</div>
		<div class="fight-status-detail__content">
			<div class="fight-status-detail__content__wrapper">
				<div
					v-if="skin === 'friendly'"
					class="fight-status-detail__content__details"
				>
					<div class="fight-status-detail__content__details__header">
						{{ translate("ui.attack_type") }}:
					</div>
					<div class="fight-status-detail__content__details__text">
						{{ damageType }}
					</div>
				</div>
				<div class="fight-status-detail__damage">
					<div class="damage-value">
						<StatsBadge
							:statsType="'swords'"
							:statsValues="attack"
							:skin="(skin + '-light') as StatsSkin"
						/>
					</div>
				</div>
				<div class="fight-status-detail__armors">
					<div class="fight-status-detail__armors-list">
						<StatsBadge
							v-for="(armorValues, armorType) in armor"
							:statsType="armorType"
							:statsValues="armorValues"
							:key="armorType"
							:skin="(skin + '-dark') as StatsSkin"
						/>
					</div>
				</div>
			</div>
		</div>
		<div
			class="fight-status-detail__left-side"
			:class="[`icon-${currentTileIcon}-60`]"
		>
			<Transition name="fade">
				<div v-if="damage">
					<div class="tint" />
					<div class="damage">-{{ damage }}</div>
				</div>
			</Transition>
			<template
				v-if="
					entity.index ===
					tilerepo.getBlockedByEnemiesMainEnemyTileIndexByTileIndex(
						entity.index,
					)
				"
			>
				<GoldenCorner top left />
				<GoldenCorner top right />
				<GoldenCorner bottom left />
				<GoldenCorner bottom right />
			</template>
		</div>
		<div class="fight-status-detail__footer">
			<div
				class="fight-status-detail__footer__line"
				v-for="(buff, i) in grantsBuff"
				:key="i"
			>
				<span class="fight-status-detail__footer__line__pretext">
					{{ buffTextData(buff).pretext }}
				</span>

				<Tooltip
					:placement="Placements.TOP"
					v-if="buffIcon(buff).length > 0"
				>
					<template #element>
						<AppMainIcon
							v-if="buffIcon(buff).length > 0"
							:iconName="buffIcon(buff)"
							:iconSize="16"
						/>
					</template>
					<template #tooltip>
						<span>
							{{ translate(buffTooltip(buff)) }}
						</span>
					</template>
				</Tooltip>
				<span class="fight-status-detail__footer__line__posttext">
					{{ buffTextData(buff).posttext }}
				</span>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import AppMainIcon from "@/components/AppMainIcon.vue";
import type { StatsSkin } from "@/components/FightSystem/StatsBadge.vue";
import StatsBadge from "@/components/FightSystem/StatsBadge.vue";
import GoldenCorner from "@/components/SVG/GoldenCorner.vue";
import Tooltip from "@/components/Tooltip.vue";
import { tilerepoFriendlySupplyUnitsFrameNames } from "@/game/entities/config";
import type TileEntity from "@/game/entities/TileEntity.ts";
import { buffIcon, buffTextData, buffTooltip } from "@/game/utils/fightsystem";
import useEmitter, {
	EmitterEvents,
	type SiegeSystemDamagePayload,
} from "@/hooks/useEmitter.ts";
import useSiegeSystemRepository from "@/hooks/useSiegeSystemRepository.ts";
import useTileRepository from "@/hooks/useTileRepository";
import {
	type ArmorOrAttackNumber,
	type ArmorOrAttackObject,
	type Buff,
} from "@/types/tile.ts";
import { Placements } from "@/types/ui";
import { formatNumber } from "@/utils/format";
import { translate } from "@/utils/translate.ts";
import { computed, markRaw, onMounted, onUnmounted, ref } from "vue";

const {
	currentTileIcon = "",
	entity,
	skin = "friendly",
} = defineProps<{
	currentTileIcon: string;
	entity: TileEntity;
	skin: "friendly" | "enemy" | "enemy-special";
}>();

const emitter = markRaw(useEmitter());
const ssr = markRaw(useSiegeSystemRepository());
const tilerepo = markRaw(useTileRepository());
const damage = ref<number | null>(null);

const currentHp = computed<number>(
	(): number => ssr.getCurrentHpByTileIndex(entity.index) ?? maxHp.value,
);

const maxHp = computed<number>(
	(): number => ssr.getMaxHpByTileIndex(entity.index) ?? 0,
);
const damageType = computed<"melee" | "ranged" | "siege" | "">(
	(): "melee" | "ranged" | "siege" | "" => {
		const armorOrAttack = (ssr.getAttackByTileIndex(entity.index) ?? 0) as
			| ArmorOrAttackNumber
			| ArmorOrAttackObject;
		if (typeof armorOrAttack === "object") {
			if (armorOrAttack.melee > 0) return "melee";
			if (armorOrAttack.ranged > 0) return "ranged";
			if (armorOrAttack.siege > 0) return "siege";
		}
		return "";
	},
);

const attack = computed<[ArmorOrAttackNumber, ArmorOrAttackNumber]>(
	(): [ArmorOrAttackNumber, ArmorOrAttackNumber] => {
		const isFriendly = ssr.isFriendlySiegeSystemUnitByFrameName(
			entity.frameName,
		);
		const attack = ssr.getAttackByTileIndex(entity.index) as
			| ArmorOrAttackNumber
			| ArmorOrAttackObject;
		const buff = ssr.getBuffsByTileIndex(entity.index)?.attack as
			| ArmorOrAttackNumber
			| ArmorOrAttackObject;

		let type: "melee" | "ranged" | "siege" | null = null;
		if (typeof attack === "object") {
			switch (true) {
				case (attack?.melee ?? 0) > 0:
					type = "melee";
					break;
				case (attack?.ranged ?? 0) > 0:
					type = "ranged";
					break;
				case (attack?.siege ?? 0) > 0:
					type = "siege";
					break;
			}
		}

		if (isFriendly) {
			if (!type) return [0, 0];
			return [
				(attack as ArmorOrAttackObject)[type] ?? 0,
				(buff as ArmorOrAttackObject)[type] ?? 0,
			];
		}

		return [
			(attack as ArmorOrAttackNumber) ?? 0,
			(buff as ArmorOrAttackNumber) ?? 0,
		];
	},
);

const armor = computed<
	| { default: [ArmorOrAttackNumber, ArmorOrAttackNumber] }
	| {
			melee: [ArmorOrAttackNumber, ArmorOrAttackNumber];
			ranged: [ArmorOrAttackNumber, ArmorOrAttackNumber];
			siege: [ArmorOrAttackNumber, ArmorOrAttackNumber];
	  }
>(
	():
		| { default: [ArmorOrAttackNumber, ArmorOrAttackNumber] }
		| {
				melee: [ArmorOrAttackNumber, ArmorOrAttackNumber];
				ranged: [ArmorOrAttackNumber, ArmorOrAttackNumber];
				siege: [ArmorOrAttackNumber, ArmorOrAttackNumber];
		  } => {
		const isFriendly = ssr.isFriendlySiegeSystemUnitByFrameName(
			entity.frameName,
		);
		const armor = ssr.getArmorByTileIndex(entity.index) as
			| ArmorOrAttackNumber
			| ArmorOrAttackObject;
		const buff = ssr.getBuffsByTileIndex(entity.index)?.armor as
			| ArmorOrAttackNumber
			| ArmorOrAttackObject;

		if (isFriendly) {
			return {
				default: [
					(armor as ArmorOrAttackNumber) ?? 0,
					(buff as ArmorOrAttackNumber) ?? 0,
				],
			};
		}

		return {
			melee: [
				(armor as ArmorOrAttackObject)?.melee ?? 0,
				(buff as ArmorOrAttackObject)?.melee ?? 0,
			],
			ranged: [
				(armor as ArmorOrAttackObject)?.ranged ?? 0,
				(buff as ArmorOrAttackObject)?.ranged ?? 0,
			],
			siege: [
				(armor as ArmorOrAttackObject)?.siege ?? 0,
				(buff as ArmorOrAttackObject)?.siege ?? 0,
			],
		};
	},
);

const grantsBuff = computed<Array<Buff>>(
	(): Array<Buff> => ssr.getGrantBuffsByTileIndex(entity.index) ?? [],
);

let timeout: ReturnType<typeof setTimeout> | null = null;

const callback = (damageValue: SiegeSystemDamagePayload): void => {
	if (!damageValue || damage.value) return;

	damage.value = damageValue;
	if (timeout) clearTimeout(timeout);
	timeout = setTimeout((): void => {
		damage.value = null;
		timeout = null;
	}, 1000);
};

onMounted((): void => {
	emitter.on(EmitterEvents.SiegeSystemDamage, callback);
});

onUnmounted((): void => {
	if (timeout) clearTimeout(timeout);
	emitter.off(EmitterEvents.SiegeSystemDamage, callback);
});
</script>

<style lang="scss" scoped>
.fight-status-detail {
	position: relative;
	width: 100%;
	@include flex-col-start;
	overflow: hidden;
	color: #f2dcb5;
	&::before {
		position: absolute;
		top: 0;
		left: 0;
		opacity: 0.6;
		// NOTE: Color-burn blending is better for dark colors
		mix-blend-mode: color-burn;
		background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
			repeat center;
		background-size: 40%;
		width: 100%;
		height: 100%;
		content: "";
	}

	.chieftain-crown {
		position: absolute;
		top: 0;
		left: -1.75rem;
		background: url($path-backgrounds + "fight-system/chieftain-crown.avif");
		background-size: cover;
		width: 8rem;
		height: 100%;
	}
	&__header {
		height: 2.3125rem;
		@include flex-row-between;
		padding: 0 0.5rem 0 4.75rem;
		width: 100%;
		font-weight: 800;

		.health-value {
			@include flex-row-end;
			gap: 0.25rem;
			font-size: 1.25rem;
			text-shadow: 0.0437rem 0.0437rem 0.0625rem rgba(0, 0, 0, 0.6);
			&__wrapper {
				@include flex-row-end;
				padding-bottom: 0.25rem;
			}
			&__total {
				color: rgb(242, 220, 181);
			}
			&__max {
				color: rgb(244, 210, 120);
			}
		}
	}
	&__content {
		width: 100%;
		@include flex-row-end;
		gap: 0.25rem;
		padding-right: 0.375rem;
		padding-bottom: 0.375rem;
		min-height: 3.625rem;
		color: rgb(242, 220, 181);
		font-size: 0.9063rem;
		line-height: 1.125;
		text-shadow: 0.0437rem 0.0437rem 0.0625rem rgba(0, 0, 0, 0.6);

		&__wrapper {
			width: 80%;
			@include flex-row-end;
			gap: 0.25rem;
		}

		&__details {
			@include flex-col-center;
			align-items: flex-end;

			&__header {
				color: rgb(242, 220, 181);
				font-size: 0.9063rem;
				text-shadow: 0.0437rem 0.0437rem 0.0625rem rgba(0, 0, 0, 0.6);
			}
			&__text {
				color: rgb(244, 210, 120);
				font-weight: 800;
				font-size: 0.9063rem;
				text-shadow: 0.0437rem 0.0437rem 0.0625rem rgba(0, 0, 0, 0.6);
				text-transform: capitalize;
			}
		}
	}
	&__damage {
		width: 25%;
		.damage-value {
			@include flex-row-end;
			gap: 0.25rem;
			color: rgb(244, 210, 120);
			font-weight: 800;
			font-size: 1.1875rem;

			&__main-value,
			&__bonus-value {
				padding-bottom: 0.25rem;

				&--positive {
					color: #19b345;
				}

				&--negative {
					color: #df101e;
				}
			}
		}
	}
	&__armors {
		width: 75%;
	}
	&__armors-list {
		@include flex-row-start;
		gap: 0.25rem;
		margin-top: 0.3125rem;
	}

	&__left-side {
		position: absolute;
		top: 0.5rem;
		left: 0.5rem;
		flex-shrink: 0;
		margin-top: -0.5rem;
		box-shadow: 0 0 0.25rem rgba(0, 0, 0, 0.6);
		width: 3.75rem;
		height: 4.40625rem;

		.damage {
			position: absolute;
			top: 0.625rem;
			right: 0;
			bottom: 0;
			left: 0;
			color: rgb(242, 220, 181);
			font-weight: 800;
			font-size: 2rem;
			@include flex-row-center;
			text-shadow: 0.0437rem 0.0437rem 0.0625rem rgba(0, 0, 0, 0.6);
			-webkit-text-stroke: 0.0625rem rgb(55, 38, 21);
			padding-bottom: 0.5rem;
		}

		.tint {
			position: absolute;
			top: 0.625rem;
			right: 0;
			bottom: 0;
			left: 0;
			background-color: rgba(207, 0, 50, 0.3);
		}

		:deep(.golden-corner) {
			width: 1rem;
			height: 1rem;
		}

		:deep(.golden-corner--top-left) {
			position: absolute;
			top: 0.625rem;
			left: 0;
		}

		:deep(.golden-corner--top-right) {
			position: absolute;
			top: 0.625rem;
			right: 0;
		}

		:deep(.golden-corner--bottom-left) {
			position: absolute;
			bottom: 0;
			left: 0;
		}

		:deep(.golden-corner--bottom-right) {
			position: absolute;
			right: 0;
			bottom: 0;
		}
	}
	&__footer {
		@include flex-col-center;
		z-index: 1;
		border: solid 0.05rem rgb(75, 75, 80);
		border-top: none;
		width: 100%;
		min-height: 1.625rem;
		color: rgb(244, 210, 120);
		text-shadow: 0.0437rem 0.0437rem 0.0625rem rgba(0, 0, 0, 0.6);

		&__line {
			@include flex-row-center;
			gap: 0.25rem;

			&__pretext {
				font-weight: 800;
				font-size: 0.9063rem;
			}
			&__posttext {
				font-size: 0.8125rem;
			}
		}
	}

	&.enemy {
		background-color: #45151d;
		.fight-status-detail__header {
			border: solid 0.05rem rgb(75, 75, 80);
			background-color: rgba(0, 0, 0, 0.4);
		}
		.fight-status-detail__content {
			border: solid 0.05rem rgb(75, 75, 80);
			border-top: none;
		}
		.fight-status-detail__footer {
			background: rgba(0, 0, 0, 0.3);
		}
	}
	&.enemy-special {
		background: linear-gradient(
			0deg,
			rgba(74, 9, 23, 1) 11%,
			rgba(171, 7, 43, 1) 100%
		);
		.fight-status-detail__header {
			border: solid 0.05rem rgb(75, 75, 80);
			background-color: rgba(0, 0, 0, 0.6);
		}
		.fight-status-detail__content {
			border: solid 0.05rem rgb(75, 75, 80);
			border-top: none;
		}
		.fight-status-detail__footer {
			background: rgba(0, 0, 0, 0.3);
		}
	}
	&.friendly {
		background-color: #1d2b3b;
		.fight-status-detail__header {
			border: solid 0.05rem rgb(75, 75, 80);
			background-color: rgba(0, 0, 0, 0.6);
		}
		.fight-status-detail__content {
			border: solid 0.05rem rgb(75, 75, 80);
			border-top: none;
		}
		.fight-status-detail__footer {
			background: rgba(0, 0, 0, 0.3);
		}
		.fight-status-detail__armors {
			width: 23%;
		}
	}
}
</style>
